import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { resendVerificationEmail } from '../firebase/auth';
import Toast from './Toast';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
    isVisible: boolean;
  }>({
    message: '',
    type: 'info',
    isVisible: false
  });
  const { login } = useAuth();
  const navigate = useNavigate();

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({
      message,
      type,
      isVisible: true
    });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }

    setIsResending(true);
    setResendMessage('');
    setError('');

    try {
      // First try to sign in to get the user, then resend verification
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      const { auth } = await import('../firebase/config');
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      if (user.emailVerified) {
        setResendMessage('Email is already verified. You can now log in.');
        return;
      }
      
      await resendVerificationEmail();
      setResendMessage('Verification email sent! Please check your inbox.');
      showToast('Verification email sent! Please check your inbox.', 'success');
      
      // Sign out the user since they're not verified
      const { signOut } = await import('firebase/auth');
      await signOut(auth);
    } catch (error: any) {
      if (error.message.includes('verify your email')) {
        // User exists but not verified, try to resend
        try {
          await resendVerificationEmail();
          setResendMessage('Verification email sent! Please check your inbox.');
          showToast('Verification email sent! Please check your inbox.', 'success');
        } catch (resendError: any) {
          const errorMessage = 'Could not resend verification email. Please try again.';
          setError(errorMessage);
          showToast(errorMessage, 'error');
        }
      } else {
        const errorMessage = error.message || 'Could not resend verification email. Please try again.';
        setError(errorMessage);
        showToast(errorMessage, 'error');
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setResendMessage('');

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (error: any) {
      setError(error.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex">
      {/* Background Image with Blur */}
      <div 
        className="fixed inset-0 w-full lg:w-[70%] h-full bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/assets/background3.png)',
          filter: 'blur(0px)',
          opacity: '0.8',
          transform: 'scale(1.00)'
        }}
      />
      
      {/* Overlay to darken the background */}
      <div className="fixed inset-0 bg-gray-900 bg-opacity-20" />
      
      {/* Left Side - Background visible (70%) */}
      <div className="relative z-10 w-0 lg:w-[70%] flex items-center justify-center p-8">
        <div className="hidden lg:block">
          {/* You can add a logo or tagline here if needed */}
        </div>
      </div>

      {/* Right Side - Login Form (30%) */}
      <div className="relative z-10 w-full lg:w-[30%] min-h-screen bg-white/40 backdrop-blur-xl shadow-2xl flex items-center justify-center p-6 lg:p-8">
        <div className="w-full max-w-md space-y-4">
          <div>
            <div className="mx-auto h-20 w-20 flex items-center justify-center">
              <img src="/assets/ledger.png" alt="Accounts System" className="h-full w-full object-contain drop-shadow-lg" />
            </div>
            <h2 className="mt-3 text-center text-lg font-semibold text-gray-900 drop-shadow-md">
              ACCOUNTS MANAGEMENT SYSTEM
            </h2>
            <p className="mt-1 text-center text-sm font-medium text-gray-800 drop-shadow-sm">
              Sign in to your account
            </p>
          </div>
          
          <form className="space-y-4" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-500/20 backdrop-blur-md border-2 border-red-400/50 text-red-900 px-4 py-3 rounded-xl font-medium">
                {error}
                {error.includes('verify your email') && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={isResending || isLoading}
                      className="text-sm bg-red-600/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      {isResending ? 'Sending...' : 'Resend Verification Email'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {resendMessage && (
              <div className="bg-green-500/20 backdrop-blur-md border-2 border-green-400/50 text-green-900 px-4 py-3 rounded-xl font-medium">
                {resendMessage}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-1 drop-shadow-sm">
                Email address
              </label>
              <div className="relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 pr-10 bg-white/60 backdrop-blur-sm border-2 border-white/40 rounded-lg shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 sm:text-sm"
                  placeholder="Enter your email"
                  disabled={isLoading}
                />
                {email && (
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setEmail('')}
                    disabled={isLoading}
                  >
                    <svg className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-900 mb-1 drop-shadow-sm">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 pr-10 bg-white/60 backdrop-blur-sm border-2 border-white/40 rounded-lg shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 sm:text-sm"
                  placeholder="Enter your password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border-2 border-orange-500/50 text-sm font-semibold rounded-lg text-white bg-orange-500/90 backdrop-blur-sm hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </div>
                ) : (
                  <>
                    <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                      <svg className="h-5 w-5 text-white/80 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </span>
                    Sign in
                  </>
                )}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm font-semibold text-orange-600 hover:text-orange-700 transition-colors drop-shadow-sm"
              >
                Forgot Password?
              </button>
            </div>
          </form>
          
          {/* Logos Section */}
          <div className="mt-2 space-y-1">
            <div className="p-4 flex items-center justify-center">
              <img src="/assets/barq_logo-transparent.png" alt="Barq Cargo System" className="h-14 object-contain" />
            </div>
            <div className="p-4 flex items-center justify-center">
              <img src="/assets/vector_logo-transparent.png" alt="Vector Logistics" className="h-14 object-contain" />
            </div>
          </div>
          
          <Toast
            message={toast.message}
            type={toast.type}
            isVisible={toast.isVisible}
            onClose={hideToast}
          />
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
